import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { apiFetch } from "@/lib/api";
import {
  WirelessDeviceLink,
  WirelessDeviceSerialLookupLink,
  formatWirelessCell,
} from "./wirelessUiShared";

type DeviceRow = {
  deviceId: number;
  imei: string;
  hubSerial: string;
  realName: string;
  status: string;
  organizationName: string;
  groupNames: string;
  lastCheckInTime: string | null;
};

const PAGE_SIZE = 50;

const COLUMNS: HubDataTableColumn<DeviceRow>[] = [
  {
    id: "hubSerial",
    header: "Device serial",
    overflow: "nowrap",
    sortValue: (row) => row.hubSerial,
    cell: (row) => <WirelessDeviceSerialLookupLink serial={row.hubSerial} />,
  },
  {
    id: "imei",
    header: "IMEI",
    overflow: "nowrap",
    sortValue: (row) => row.imei,
    cell: (row) => <WirelessDeviceLink deviceId={row.deviceId} label={row.imei || "—"} className="font-mono" />,
  },
  {
    id: "realName",
    header: "Name",
    sortValue: (row) => row.realName,
    cell: (row) => row.realName || "—",
  },
  {
    id: "organizationName",
    header: "Organization",
    sortValue: (row) => row.organizationName,
    cell: (row) => row.organizationName,
  },
  {
    id: "groupNames",
    header: "Groups",
    sortValue: (row) => row.groupNames,
    cell: (row) => row.groupNames,
  },
  {
    id: "status",
    header: "Status",
    overflow: "nowrap",
    sortValue: (row) => row.status,
    cell: (row) => row.status,
  },
  {
    id: "lastCheckInTime",
    header: "Last check-in",
    overflow: "nowrap",
    sortValue: (row) => row.lastCheckInTime ?? "",
    cell: (row) => formatWirelessCell(row.lastCheckInTime),
  },
];

export function WirelessDevicesPage() {
  const [query, setQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [page, setPage] = useState(0);

  const devicesQuery = useQuery({
    queryKey: ["wireless", "devices", appliedQuery, page],
    queryFn: () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (appliedQuery.trim() !== "") {
        params.set("q", appliedQuery.trim());
      }
      return apiFetch<{ devices: DeviceRow[]; total: number }>(`/api/wireless/devices?${params.toString()}`);
    },
  });

  const devices = devicesQuery.data?.devices ?? [];
  const total = devicesQuery.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);

  return (
    <div className="space-y-4">
      <PageTitleWithHelp hoverHint="Active wireless portal devices with organization and group membership. Device serial opens manufacturing device lookup. IMEI opens the wireless portal device page.">
        Devices
      </PageTitleWithHelp>

      <Card>
        <CardBody className="flex flex-wrap items-end gap-3 py-3">
          <div className="min-w-[16rem] flex-1">
            <label
              className="block text-xs font-medium uppercase tracking-wide text-slate-600"
              htmlFor="wireless-device-search"
            >
              Search
            </label>
            <Input
              id="wireless-device-search"
              value={query}
              placeholder="Name, IMEI, or device serial"
              className="mt-1"
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <Button
            type="button"
            onClick={() => {
              setAppliedQuery(query);
              setPage(0);
            }}
          >
            Search
          </Button>
        </CardBody>
      </Card>

      <HubDataTable
        title="Devices"
        columns={COLUMNS}
        rows={devices}
        rowKey={(row) => row.deviceId}
        loading={devicesQuery.isLoading}
        loadingMessage="Loading devices..."
        error={devicesQuery.isError ? "Could not load devices." : null}
        empty="No devices."
        isFiltered={appliedQuery.trim() !== ""}
        filteredEmpty="No devices match this search."
      />

      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>
          {total} device{total === 1 ? "" : "s"}
        </span>
        <div className="flex items-center gap-2">
          <Button type="button" variant="secondary" disabled={safePage <= 0} onClick={() => setPage(safePage - 1)}>
            Previous
          </Button>
          <span>
            Page {safePage + 1} of {pageCount}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={safePage >= pageCount - 1}
            onClick={() => setPage(safePage + 1)}
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
